// i18n + auth context
import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import type { Locale } from './api';
import { configureApi } from './api';

const dict: Record<Locale, Record<string, any>> = {
  ar: {},
  en: {},
};

// Lazy-load dictionaries
import dictAr from './locales/ar.json';
import dictEn from './locales/en.json';
dict.ar = dictAr;
dict.en = dictEn;

type AuthState = {
  token: string | null;
  refresh?: string | null;
  user: any | null;
  roles: string[];
};

type Ctx = {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, params?: Record<string, any>) => string;
  auth: AuthState;
  setAuth: (a: AuthState) => void;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { email: string; password: string; full_name: string; phone?: string }) => Promise<void>;
  logout: () => void;
};

const AppCtx = createContext<Ctx | null>(null);

const STORAGE_KEY = 'safar_state_v1';

function loadState(): { locale: Locale; auth: AuthState } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const s = JSON.parse(raw);
      return { locale: s.locale || 'ar', auth: s.auth || { token: null, user: null, roles: [] } };
    }
  } catch {}
  return { locale: 'ar', auth: { token: null, user: null, roles: [] } };
}

function saveState(state: { locale: Locale; auth: AuthState }) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
}

export function AppProvider({ children }: { children: ReactNode }) {
  const initial = loadState();
  const [locale, setLocaleState] = useState<Locale>(initial.locale);
  const [auth, setAuth] = useState<AuthState>(initial.auth);
  const authRef = useRef<AuthState>(initial.auth);
  authRef.current = auth;

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr';
  }, [locale]);

  useEffect(() => { saveState({ locale, auth }); }, [locale, auth]);

  useEffect(() => {
    configureApi({
      getToken: () => authRef.current.token, getLocale: () => locale,
      refresh: async () => {
        const rt = authRef.current.refresh; if (!rt) return null;
        try {
          const r = await fetch('/api/auth/refresh', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refresh_token: rt }) });
          if (!r.ok) return null; const j = await r.json(); const tok = j?.data?.access_token; if (!tok) return null;
          authRef.current = { ...authRef.current, token: tok }; setAuth(a => ({ ...a, token: tok })); return tok;
        } catch { return null; }
      },
      onExpired: () => { authRef.current = { token: null, refresh: null, user: null, roles: [] }; setAuth({ token: null, refresh: null, user: null, roles: [] }); },
    });
  }, [auth.token, locale]);

  const t = (key: string, params?: Record<string, any>) => {
    const get = (k: string) => k.split('.').reduce<any>((o, p) => (o ? o[p] : null), dict[locale]);
    let value = get(key);
    if (value == null) {
      // fallback to other locale
      value = get(key.replace(/\.ar$/, '.en')) || (locale === 'ar' ? get(key + '.en') : null);
    }
    if (value == null) return key;
    if (typeof value === 'string' && params) {
      for (const [k, v] of Object.entries(params)) value = value.replace(new RegExp(`{${k}}`, 'g'), String(v));
    }
    return value;
  };

  const setLocale = (l: Locale) => setLocaleState(l);

  const login = async (email: string, password: string) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error?.message_en || 'Login failed');
    setAuth({ token: data.data.access_token, refresh: data.data.refresh_token || null, user: data.data.user, roles: data.data.roles || [] });
  };

  const register = async (data: { email: string; password: string; full_name: string; phone?: string }) => {
    const res = await fetch('/api/auth/register', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json?.error?.message_en || 'Registration failed');
    setAuth({ token: json.data.access_token, refresh: json.data.refresh_token || null, user: json.data.user, roles: ['customer'] });
  };

  const logout = async () => {
    try {
      if (auth.token) {
        await fetch('/api/auth/logout', { method: 'POST', headers: { Authorization: `Bearer ${auth.token}` } });
      }
    } catch {}
    setAuth({ token: null, user: null, roles: [] });
  };

  return (
    <AppCtx.Provider value={{ locale, setLocale, t, auth, setAuth, login, register, logout }}>
      {children}
    </AppCtx.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppCtx);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
}

export function useT() {
  return useApp().t;
}
